"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { captureActivityPlainData } from "@/app/activity/domain/activityPlainDataCapture";
import { createBookingDraftId } from "@/app/booking/core/draftId";
import { captureCustomerOwnedSessionStorageMutationProof, commitCustomerOwnedSessionStorageMutation, readCustomerOwnedSessionStorageRawValueFromCapturedReader, readCustomerOwnedSessionStorageRawValueStable } from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import { validateProductPurchaseReceipt, type ProductPurchaseReceiptV2 } from "@/app/commerce/products/productPurchaseReceipt";
import { sameProductOrderData } from "@/app/commerce/products/productCurrentOrder";

import {
  getLocationStoreProductMaximumQuantity,
  getLocationStore,
  type LocationStoreCartLine,
  type LocationStoreProduct,
} from "@/app/locations/locationStore";

/**
 * Client-side store cart, persisted per location in the browser like the
 * favourite-locations pattern. Ordinary storage keeps product IDs and
 * quantities; checkout upgrades the same envelope with minimal unit/receipt
 * provenance, never contact, addresses or payment details. Lines re-join the published catalog on read so prices and
 * copy always come from the single catalog source and unknown or retired
 * product IDs drop silently instead of rendering stale data.
 */

const STORAGE_KEY = "volera-location-store-carts";
const CART_CHANGE_EVENT = "volera-location-store-cart-change";

export interface LocationStoreCartQuantityLine {
  productId: string;
  quantity: number;
}

type StoredCartItem = LocationStoreCartQuantityLine;

type StoredCarts = Record<string, StoredCartItem[]>;

interface CartUnits { productId: string; productVersion: string; generation: string; first: number; next: number }
export interface LocationStoreCartPurchaseSource {
  draftId: string; locationId: string; kind: "cart" | "no-cart";
  lines: readonly { productId: string; productVersion: string; quantity: number; units: CartUnits | null }[];
}
interface CartReceiptMarker {
  draftId: string; customerAccountId: string; providerId: string; orderId: string; receiptId: string; paymentReferenceId: string;
  source: LocationStoreCartPurchaseSource;
}
interface CartEnvelope {
  schemaVersion: 2; kind: "location-store-carts"; carts: StoredCarts;
  units: Record<string, CartUnits[]>; purchaseSources: LocationStoreCartPurchaseSource[]; receipts: CartReceiptMarker[];
}
const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const isId = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 512 && value.trim() === value && !/[\u0000-\u001f\u007f]/u.test(value);
const isUnits = (value: unknown): value is CartUnits => isRecord(value) && Object.keys(value).length === 5 && isId(value.productId) && isId(value.productVersion) && isId(value.generation) && Number.isSafeInteger(value.first) && Number.isSafeInteger(value.next) && (value.first as number) >= 0 && (value.next as number) > (value.first as number);

function validSource(value: unknown): value is LocationStoreCartPurchaseSource {
  if (!isRecord(value) || Object.keys(value).length !== 4 || !isId(value.draftId) || !isId(value.locationId) || (value.kind !== "cart" && value.kind !== "no-cart") || !Array.isArray(value.lines) || value.lines.length < 1 || value.lines.length > 100) return false;
  const ids = new Set<string>();
  return value.lines.every(line => {
    if (!isRecord(line) || Object.keys(line).length !== 4 || !isId(line.productId) || !isId(line.productVersion) || !Number.isSafeInteger(line.quantity) || (line.quantity as number) <= 0 || ids.has(line.productId)) return false;
    ids.add(line.productId);
    return value.kind === "no-cart" ? line.units === null : isUnits(line.units) && line.units.productId === line.productId && line.units.productVersion === line.productVersion && line.units.next - line.units.first === line.quantity;
  });
}

function parseEnvelope(value: unknown): CartEnvelope | null {
  if (!isRecord(value) || Object.keys(value).length !== 6 || value.schemaVersion !== 2 || value.kind !== "location-store-carts" || !isRecord(value.carts) || !isRecord(value.units) || !Array.isArray(value.purchaseSources) || value.purchaseSources.length > 1024 || !Array.isArray(value.receipts) || value.receipts.length > 4096) return null;
  const carts = parseStoredCarts(value.carts);
  if (!sameProductOrderData(carts, value.carts) || Object.keys(carts).length > 100 || Object.keys(value.units).length !== Object.keys(carts).length) return null;
  for (const [locationId, items] of Object.entries(carts)) {
    const units = value.units[locationId];
    if (!isId(locationId) || !Array.isArray(units) || units.length !== items.length || items.length > 100 || !items.every(item => Object.keys(item).length === 2 && isId(item.productId) && Number.isSafeInteger(item.quantity) && item.quantity > 0 && item.quantity <= 10_000) || new Set(items.map(row => row.productId)).size !== items.length || !units.every((unit, index) => isUnits(unit) && unit.productId === items[index].productId && unit.next - unit.first === items[index].quantity)) return null;
  }
  if (!value.purchaseSources.every(validSource) || new Set(value.purchaseSources.map(source => source.draftId)).size !== value.purchaseSources.length) return null;
  const markers = value.receipts;
  if (!markers.every(marker => isRecord(marker) && Object.keys(marker).length === 7 && [marker.draftId, marker.customerAccountId, marker.providerId, marker.orderId, marker.receiptId, marker.paymentReferenceId].every(isId) && validSource(marker.source) && marker.source.draftId === marker.draftId)) return null;
  for (const key of ["draftId", "orderId", "receiptId", "paymentReferenceId"] as const) if (new Set(markers.map(marker => marker[key])).size !== markers.length) return null;
  if (value.purchaseSources.some(source => markers.some(marker => marker.draftId === source.draftId))) return null;
  return value as unknown as CartEnvelope;
}

function createUnits(locationId: string, item: StoredCartItem): CartUnits | null {
  const product = getLocationStore(locationId)?.products.find(row => row.productId === item.productId);
  return product ? { productId: item.productId, productVersion: product.productVersion, generation: createBookingDraftId("commerce"), first: 0, next: item.quantity } : null;
}

function replaceEnvelopeCart(envelope: CartEnvelope, locationId: string, items: StoredCartItem[]): CartEnvelope | null {
  const previous = envelope.units[locationId] ?? [];
  const units: CartUnits[] = [];
  for (const item of items) {
    const version = getLocationStore(locationId)?.products.find(row => row.productId === item.productId)?.productVersion;
    const prior = previous.find(row => row.productId === item.productId && row.productVersion === version);
    const oldQuantity = prior ? prior.next - prior.first : 0;
    const next = prior ? { ...prior, first: prior.first + Math.max(0, oldQuantity - item.quantity), next: prior.next + Math.max(0, item.quantity - oldQuantity) } : createUnits(locationId, item);
    if (!next || !isUnits(next)) return null;
    units.push(next);
  }
  const next = { ...envelope, carts: { ...envelope.carts }, units: { ...envelope.units } };
  if (items.length) { next.carts[locationId] = items; next.units[locationId] = units; }
  else { delete next.carts[locationId]; delete next.units[locationId]; }
  return next;
}

function readStrictCartEnvelope() {
  const storage = getBrowserStorage();
  if (!storage || hasUnpersistedCartChanges) return null;
  const result = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: STORAGE_KEY });
  if (result.status !== "ready" || (result.rawValue?.length ?? 0) > 4_000_000) return null;
  try {
    const captured = captureActivityPlainData(result.rawValue === null ? {} : JSON.parse(result.rawValue));
    if (!captured.valid || !isRecord(captured.value)) return null;
    let envelope: CartEnvelope | null;
    if ("schemaVersion" in captured.value) envelope = parseEnvelope(captured.value);
    else {
      const carts = parseStoredCarts(captured.value);
      if (!sameProductOrderData(carts, captured.value)) return null;
      envelope = { schemaVersion: 2, kind: "location-store-carts", carts, units: {}, purchaseSources: [], receipts: [] };
      for (const [locationId, items] of Object.entries(carts)) {
        const units = items.map(item => createUnits(locationId, item));
        if (units.some(unit => !unit)) return null;
        envelope.units[locationId] = units as CartUnits[];
      }
      envelope = parseEnvelope(envelope);
    }
    const final = readCustomerOwnedSessionStorageRawValueFromCapturedReader({ storage, storageKey: STORAGE_KEY });
    return envelope && getBrowserStorage() === storage && final.status === "ready" && final.rawValue === result.rawValue ? { storage, raw: result.rawValue, envelope } : null;
  } catch { return null; }
}

function commitCartEnvelope(source: NonNullable<ReturnType<typeof readStrictCartEnvelope>>, envelope: CartEnvelope, isCurrent: () => boolean) {
  try {
    if (!parseEnvelope(envelope) || !isCurrent() || getBrowserStorage() !== source.storage || hasUnpersistedCartChanges) return false;
    const nextRawValue = JSON.stringify(envelope);
    if (nextRawValue.length > 4_000_000) return false;
    const result = commitCustomerOwnedSessionStorageMutation({ storage: source.storage, storageKey: STORAGE_KEY, expectedCurrentRawValue: source.raw, nextRawValue,
      mutationProof: captureCustomerOwnedSessionStorageMutationProof(), preserveCurrentRawValueOnStale: true, mutationIsCurrent: () => getBrowserStorage() === source.storage && !hasUnpersistedCartChanges && isCurrent() });
    if (result.status !== "committed") return false;
    memoryCarts = envelope.carts; memoryCartsInitialized = true; hasUnpersistedCartChanges = false;
    window.dispatchEvent(new Event(CART_CHANGE_EVENT));
    const current = readStrictCartEnvelope();
    return Boolean(current && current.storage === source.storage && current.raw === nextRawValue && isCurrent());
  } catch { return false; }
}

/** Auth teardown audits only minimal IDs/unit provenance, never deleting a
 * receipt marker or exposing another customer's historical order contact. */
export function verifyLocationStoreCartPurchaseMetadata() {
  const storage = getBrowserStorage();
  if (!storage) return false;
  const source = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: STORAGE_KEY });
  if (source.status !== "ready" || (source.rawValue?.length ?? 0) > 4_000_000) return false;
  try {
    if (source.rawValue === null) return true;
    const value: unknown = JSON.parse(source.rawValue);
    if (isRecord(value) && "schemaVersion" in value && !parseEnvelope(value)) return false;
    const final = readCustomerOwnedSessionStorageRawValueStable({ storage, storageKey: STORAGE_KEY });
    return getBrowserStorage() === storage && final.status === "ready" && final.rawValue === source.rawValue;
  } catch { return false; }
}

const EMPTY_LINES: readonly LocationStoreCartLine[] = [];
let memoryCarts: StoredCarts = {};
let memoryCartsInitialized = false;
let hasUnpersistedCartChanges = false;
let observedStorage: Storage | null = null;

function getBrowserStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storage = window.localStorage;

    // Test environments can replace the Storage object between cases. The
    // same reset also prevents a newly installed browser storage provider
    // from inheriting stale module memory.
    if (storage !== observedStorage) {
      observedStorage = storage;
      memoryCarts = {};
      memoryCartsInitialized = false;
      hasUnpersistedCartChanges = false;
    }

    return storage;
  } catch {
    return null;
  }
}

function normalizeStoredItems(items: unknown): StoredCartItem[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(
    (item): item is StoredCartItem =>
      Boolean(item) &&
      typeof (item as StoredCartItem).productId === "string" &&
      Number.isInteger((item as StoredCartItem).quantity) &&
      (item as StoredCartItem).quantity > 0,
  );
}

function parseStoredCarts(value: unknown): StoredCarts {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).flatMap(([locationId, items]) => {
      const normalizedItems = normalizeStoredItems(items);
      return normalizedItems.length > 0
        ? [[locationId, normalizedItems] as const]
        : [];
    }),
  );
}

function readPersistedCarts(storage: Storage) {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const value = raw ? JSON.parse(raw) : {};
    const envelope = isRecord(value) && "schemaVersion" in value ? parseEnvelope(value) : undefined;
    if (envelope === null) return { carts: {} as StoredCarts, succeeded: false } as const;
    return {
      carts: envelope ? envelope.carts : parseStoredCarts(value),
      succeeded: true,
    } as const;
  } catch {
    return { carts: {} as StoredCarts, succeeded: false } as const;
  }
}

/**
 * Persisted data is authoritative while storage is healthy. Only the legacy
 * quantity-only cart may retain failed writes in memory. Once checkout owns
 * unit generations, ordinary edits need the same durable envelope as receipt
 * finalization and may not replace it with an optimistic quantity-only view.
 */
function readStoredCarts(): StoredCarts {
  const storage = getBrowserStorage();

  if (hasUnpersistedCartChanges || !storage) {
    return memoryCarts;
  }

  const persisted = readPersistedCarts(storage);

  if (persisted.succeeded) {
    memoryCarts = persisted.carts;
    memoryCartsInitialized = true;
  } else if (!memoryCartsInitialized) {
    memoryCarts = {};
    memoryCartsInitialized = true;
  }

  return memoryCarts;
}

function writeStoredItems(
  locationId: string,
  items: StoredCartItem[],
  expectedItems?: StoredCartItem[],
  mutationIsCurrent?: () => boolean,
): boolean {
  if (typeof window === "undefined" || (mutationIsCurrent && !mutationIsCurrent())) {
    return false;
  }

  const storage = getBrowserStorage();
  // Once a checkout companion exists, every ordinary mutation preserves its
  // unit generations and receipt markers in the same envelope. Reject a failed
  // edit without dirtying the in-memory fallback: the next healthy edit must
  // remain retryable, and an exact nested/concurrent winner must remain intact.
  let versioned = false;
  try { const raw = storage?.getItem(STORAGE_KEY); const value: unknown = raw ? JSON.parse(raw) : null; versioned = isRecord(value) && "schemaVersion" in value; } catch { versioned = true; }
  if (versioned || mutationIsCurrent) {
    const source = readStrictCartEnvelope();
    const sourceMatches = source && (!expectedItems || sameProductOrderData(source.envelope.carts[locationId] ?? [], expectedItems));
    const next = sourceMatches ? replaceEnvelopeCart(source.envelope, locationId, items) : null;
    if (source && next && commitCartEnvelope(source, next, mutationIsCurrent ?? (() => true))) return true;
    // Rejoin the persisted winner for presentation only. This does not retry
    // the rejected operation, rewrite unit intervals, or erase any companion.
    readStoredCarts();
    window.dispatchEvent(new Event(CART_CHANGE_EVENT));
    return false;
  }
  const carts = { ...readStoredCarts() };

  if (items.length > 0) {
    carts[locationId] = items;
  } else {
    delete carts[locationId];
  }

  memoryCarts = carts;
  memoryCartsInitialized = true;

  try {
    if (!storage) {
      throw new Error("Browser storage is unavailable.");
    }

    storage.setItem(STORAGE_KEY, JSON.stringify(carts));
    hasUnpersistedCartChanges = false;
  } catch {
    // Private-mode, disabled, or full storage: session memory keeps the cart
    // usable and a later mutation retries persistence with the full state.
    hasUnpersistedCartChanges = true;
  }

  window.dispatchEvent(new Event(CART_CHANGE_EVENT));
  return true;
}

function joinCartLines(locationId: string): readonly LocationStoreCartLine[] {
  const store = getLocationStore(locationId);

  if (
    !store ||
    !store.onlinePurchase.isPublished ||
    !store.onlinePurchase.isPurchasable ||
    store.onlinePurchase.purchaseProviderState !== "ready" ||
    !store.onlinePurchase.providerId
  ) {
    return EMPTY_LINES;
  }

  const productsById = new Map(
    store.products.map((product) => [product.productId, product]),
  );
  const seenProductIds = new Set<string>();
  const lines = normalizeStoredItems(readStoredCarts()[locationId]).flatMap(
    (item) => {
      if (seenProductIds.has(item.productId)) {
        return [];
      }

      seenProductIds.add(item.productId);
      const product = productsById.get(item.productId);
      const maximumQuantity = product
        ? getLocationStoreProductMaximumQuantity(product)
        : 0;

      return product && maximumQuantity > 0
        ? [
            {
              product,
              quantity: Math.min(item.quantity, maximumQuantity),
            },
          ]
        : [];
    },
  );

  return lines.length > 0 ? lines : EMPTY_LINES;
}

function subscribeToCart(onChange: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener(CART_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);

  return () => {
    window.removeEventListener(CART_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function readLocationStoreCartLines(locationId: string) {
  const lines = joinCartLines(locationId);
  const canonicalItems = lines.map((line) => ({
    productId: line.product.productId,
    quantity: line.quantity,
  }));
  const storedItems = normalizeStoredItems(
    readStoredCarts()[locationId],
  );

  if (
    JSON.stringify(storedItems) !== JSON.stringify(canonicalItems)
  ) {
    writeStoredItems(locationId, canonicalItems, storedItems);
  }

  return lines;
}

function getMutableCartItems(locationId: string) {
  return joinCartLines(locationId).map((line) => ({
    productId: line.product.productId,
    quantity: line.quantity,
  }));
}

function resolvePurchasableProduct(
  locationId: string,
  productId: string,
): {
  product: LocationStoreProduct;
  maximumQuantity: number;
} | null {
  const store = getLocationStore(locationId);
  const product = store?.products.find(
    (candidate) => candidate.productId === productId,
  );
  const maximumQuantity = product
    ? getLocationStoreProductMaximumQuantity(product)
    : 0;

  return product && maximumQuantity > 0
    ? { product, maximumQuantity }
    : null;
}

export function addLocationStoreCartItem(locationId: string, productId: string, mutationIsCurrent?: () => boolean) {
  const resolution = resolvePurchasableProduct(locationId, productId);

  if (!resolution) {
    return false;
  }

  const items = getMutableCartItems(locationId);
  const existing = items.find((item) => item.productId === productId);
  const nextQuantity = Math.min(
    (existing?.quantity ?? 0) + 1,
    resolution.maximumQuantity,
  );

  if (existing?.quantity === nextQuantity) {
    return false;
  }

  return writeStoredItems(
    locationId,
    existing
      ? items.map((item) =>
          item.productId === productId
            ? { ...item, quantity: nextQuantity }
            : item,
        )
      : [...items, { productId, quantity: 1 }],
    items,
    mutationIsCurrent,
  );
}

export function changeLocationStoreCartQuantity(
  locationId: string,
  productId: string,
  delta: number,
  mutationIsCurrent?: () => boolean,
) {
  if (!Number.isSafeInteger(delta) || delta === 0) {
    return false;
  }

  const resolution = resolvePurchasableProduct(locationId, productId);
  const items = getMutableCartItems(locationId);
  const existing = items.find((item) => item.productId === productId);

  if (!resolution || !existing) {
    return false;
  }

  const quantity = Math.min(
    existing.quantity + delta,
    resolution.maximumQuantity,
  );

  if (quantity === existing.quantity) {
    return false;
  }

  return writeStoredItems(
    locationId,
    items.flatMap((item) => {
      if (item.productId !== productId) {
        return [item];
      }

      return quantity > 0 ? [{ ...item, quantity }] : [];
    }),
    items,
    mutationIsCurrent,
  );
}

export function clearLocationStoreCart(locationId: string) {
  return writeStoredItems(locationId, []);
}

/** Capture at Store Checkout, before creating its anonymous draft. A no-cart
 * purchase is an explicit caller choice and is refused while a cart exists. */
export function captureLocationStoreCartPurchaseSource(input: {
  draftId: string; locationId: string; lines: readonly { productId: string; productVersion: string; quantity: number }[];
  kind?: "cart" | "no-cart";
  requireAbsent?: boolean;
}): LocationStoreCartPurchaseSource | null {
  const captured = captureActivityPlainData(input);
  if (!captured.valid || !isRecord(captured.value)) return null;
  input = captured.value as unknown as typeof input;
  const source = readStrictCartEnvelope();
  if (!source || !isId(input.draftId) || !isId(input.locationId) || !Array.isArray(input.lines) || input.lines.length < 1 || input.lines.length > 100 || !input.lines.every(line => isRecord(line) && Object.keys(line).length === 3 && isId(line.productId) && isId(line.productVersion) && typeof line.quantity === "number" && Number.isSafeInteger(line.quantity) && line.quantity > 0 && line.quantity <= 10_000)) return null;
  const kind = input.kind ?? "cart";
  const cart = source.envelope.carts[input.locationId] ?? [];
  const units = source.envelope.units[input.locationId] ?? [];
  if (kind === "no-cart" ? cart.length !== 0 : !sameProductOrderData(cart, input.lines.map(line => ({ productId: line.productId, quantity: line.quantity })))) return null;
  const purchaseSource: LocationStoreCartPurchaseSource = { draftId: input.draftId, locationId: input.locationId, kind,
    lines: input.lines.map(line => ({ ...line, units: kind === "no-cart" ? null : units.find(unit => unit.productId === line.productId) ?? null })) };
  if (!validSource(purchaseSource)) return null;
  const existing = source.envelope.purchaseSources.find(row => row.draftId === input.draftId);
  if (existing) return !input.requireAbsent && sameProductOrderData(existing, purchaseSource) ? existing : null;
  if (source.envelope.receipts.some(marker => marker.draftId === input.draftId) || source.envelope.purchaseSources.length >= 1024) return null;
  return commitCartEnvelope(source, { ...source.envelope, purchaseSources: [...source.envelope.purchaseSources, purchaseSource] }, () => true) ? purchaseSource : null;
}

/** Exact caller-known handoff lookup for Back/Close cleanup. It exposes no
 * receipt history and a completed marker is never treated as an unused source. */
export function inspectLocationStoreCartPurchaseSource(input: {
  draftId: string; locationId: string; lines: readonly { productId: string; productVersion: string; quantity: number }[];
}): { status: "present"; source: LocationStoreCartPurchaseSource } | { status: "absent" | "unavailable" } {
  const captured = captureActivityPlainData(input);
  if (!captured.valid || !isRecord(captured.value)) return { status: "unavailable" };
  input = captured.value as unknown as typeof input;
  if (!isId(input.draftId) || !isId(input.locationId) || !Array.isArray(input.lines)) return { status: "unavailable" };
  const current = readStrictCartEnvelope();
  if (!current || current.envelope.receipts.some(marker => marker.draftId === input.draftId)) return { status: "unavailable" };
  const source = current.envelope.purchaseSources.find(row => row.draftId === input.draftId);
  if (!source) return { status: "absent" };
  return source.locationId === input.locationId && sameProductOrderData(source.lines.map(({ productId, productVersion, quantity }) => ({ productId, productVersion, quantity })), input.lines)
    ? { status: "present", source } : { status: "unavailable" };
}

/** Read-only provenance for a Product benefit editor's cart-scoped intent.
 * Unit generations distinguish a continued cart from Clear → Add and from
 * additional units left after an accepted order. This grants no pricing. */
export function readLocationStoreCartBenefitUnits(locationId: string):
  { status: 'ready'; units: readonly CartUnits[] } | { status: 'unavailable' } {
  const source = readStrictCartEnvelope();
  return source ? { status: 'ready', units: (source.envelope.units[locationId] ?? []).map(unit => ({ ...unit })) }
    : { status: 'unavailable' };
}

export function readLocationStoreCartActivePurchaseSources(locationId: string):
  { status: 'ready'; sources: readonly LocationStoreCartPurchaseSource[] } | { status: 'unavailable' } {
  const source = readStrictCartEnvelope();
  if (!source) return { status: 'unavailable' };
  const units = source.envelope.units[locationId] ?? [];
  return { status: 'ready', sources: source.envelope.purchaseSources.filter(purchase => purchase.locationId === locationId &&
    purchase.kind === 'cart' && purchase.lines.some(line => line.units && units.some(unit => unit.productId === line.productId &&
      unit.productVersion === line.productVersion && unit.generation === line.units!.generation &&
      unit.first < line.units!.next && unit.next > line.units!.first))) };
}

export function validateLocationStoreCartBenefitSource(value: unknown): LocationStoreCartPurchaseSource | null {
  const captured = captureActivityPlainData(value);
  return captured.valid && validSource(captured.value) ? captured.value as LocationStoreCartPurchaseSource : null;
}

/** Roll back only the exact just-created handoff; never restores old cart
 * bytes over an addition or removes another draft's pending companion. */
export function removeLocationStoreCartPurchaseSource(expected: LocationStoreCartPurchaseSource, isCurrent: () => boolean): boolean {
  const captured = captureActivityPlainData(expected);
  if (!captured.valid || !validSource(captured.value)) return false;
  expected = captured.value;
  const source = readStrictCartEnvelope();
  if (!source || source.envelope.receipts.some(marker => marker.draftId === expected.draftId)) return false;
  const existing = source.envelope.purchaseSources.find(row => row.draftId === expected.draftId);
  return Boolean(existing && sameProductOrderData(existing, expected) && commitCartEnvelope(source, { ...source.envelope, purchaseSources: source.envelope.purchaseSources.filter(row => row.draftId !== expected.draftId) }, isCurrent));
}

function receiptMarker(receipt: ProductPurchaseReceiptV2, source: LocationStoreCartPurchaseSource): CartReceiptMarker | null {
  const lines = source.lines.map(({ productId, productVersion, quantity }) => ({ productId, productVersion, quantity }));
  if (source.draftId !== receipt.draftId || source.locationId !== receipt.purchaseDraft.locationId || !sameProductOrderData(lines, receipt.purchaseDraft.lines.map(({ productId, productVersion, quantity }) => ({ productId, productVersion, quantity })))) return null;
  return { draftId: receipt.draftId, customerAccountId: receipt.customerAccountId, providerId: receipt.purchaseDraft.providerId, orderId: receipt.providerOrderId, receiptId: receipt.providerReceiptId, paymentReferenceId: receipt.paymentReferenceId, source };
}

export function isLocationStoreCartPurchaseFinalized(receipt: ProductPurchaseReceiptV2): boolean {
  const captured = captureActivityPlainData(receipt);
  if (!captured.valid) return false;
  const valid = validateProductPurchaseReceipt(captured.value);
  if (!valid.valid) return false;
  receipt = valid.receipt;
  const source = readStrictCartEnvelope();
  const marker = source?.envelope.receipts.find(row => row.draftId === receipt.draftId);
  return Boolean(marker && sameProductOrderData(marker, receiptMarker(receipt, marker.source)));
}

/** Independent provider acceptance must be proven by the caller's lease.
 * Cart decrement and exact receipt marker are one strict envelope mutation.
 * Unit intervals preserve Clear→Add and decrement→Add, not just extra counts. */
export function finalizeLocationStoreCartPurchase(input: { receipt: ProductPurchaseReceiptV2; isCurrentOwner: () => boolean }): boolean {
  try {
  const captured = captureActivityPlainData(input);
  if (!captured.valid || !isRecord(captured.value) || typeof captured.value.isCurrentOwner !== "function") return false;
  input = captured.value as unknown as typeof input;
  const validation = validateProductPurchaseReceipt(input.receipt);
  if (!validation.valid || !input.isCurrentOwner()) return false;
  const receipt = validation.receipt;
  const source = readStrictCartEnvelope();
  if (!source) return false;
  const prior = source.envelope.receipts.find(row => row.draftId === receipt.draftId || row.orderId === receipt.providerOrderId || row.receiptId === receipt.providerReceiptId || row.paymentReferenceId === receipt.paymentReferenceId);
  if (prior) return sameProductOrderData(prior, receiptMarker(receipt, prior.source)) && input.isCurrentOwner();
  const companion = source.envelope.purchaseSources.find(row => row.draftId === receipt.draftId);
  const marker = companion ? receiptMarker(receipt, companion) : null;
  if (!companion || !marker || source.envelope.receipts.length >= 4096) return false;
  const envelope: CartEnvelope = { ...source.envelope, carts: { ...source.envelope.carts }, units: { ...source.envelope.units }, purchaseSources: source.envelope.purchaseSources.filter(row => row.draftId !== receipt.draftId), receipts: [...source.envelope.receipts, marker] };
  const locationId = companion.locationId;
  const nextUnits = (envelope.units[locationId] ?? []).flatMap(unit => {
    const purchased = companion.lines.find(line => line.productId === unit.productId)?.units;
    if (!purchased || purchased.generation !== unit.generation || purchased.productVersion !== unit.productVersion) return [unit];
    const first = Math.min(unit.next, Math.max(unit.first, purchased.next));
    return first < unit.next ? [{ ...unit, first }] : [];
  });
  if (nextUnits.length) { envelope.units[locationId] = nextUnits; envelope.carts[locationId] = nextUnits.map(unit => ({ productId: unit.productId, quantity: unit.next - unit.first })); }
  else { delete envelope.units[locationId]; delete envelope.carts[locationId]; }
    return commitCartEnvelope(source, envelope, input.isCurrentOwner);
  } catch { return false; }
}

/**
 * Legacy quantity-only compatibility helper. It is not receipt-idempotent and
 * must not finalize a V2 purchase; use finalizeLocationStoreCartPurchase.
 */
export function consumeLocationStoreCartLines(
  locationId: string,
  purchasedLines: readonly LocationStoreCartQuantityLine[],
) {
  const seenProductIds = new Set<string>();

  if (purchasedLines.length === 0) {
    return false;
  }

  for (const { productId, quantity } of purchasedLines) {
    if (
      productId !== productId.trim() ||
      !productId ||
      !Number.isSafeInteger(quantity) ||
      quantity <= 0 ||
      seenProductIds.has(productId)
    ) {
      return false;
    }

    seenProductIds.add(productId);
  }

  const purchasedQuantities = new Map(
    purchasedLines.map(({ productId, quantity }) => [
      productId,
      quantity,
    ]),
  );
  const items = getMutableCartItems(locationId);
  const nextItems = items.flatMap((item) => {
    const purchasedQuantity =
      purchasedQuantities.get(item.productId) ?? 0;
    const quantity = item.quantity - purchasedQuantity;

    return quantity > 0 ? [{ ...item, quantity }] : [];
  });

  if (JSON.stringify(items) !== JSON.stringify(nextItems)) {
    return writeStoredItems(locationId, nextItems, items);
  }

  return true;
}

/**
 * Lines re-materialize only when the underlying stored payload changes, so
 * useSyncExternalStore consumers keep referential stability between events.
 */
const lineCache = new Map<
  string,
  { serialized: string; lines: readonly LocationStoreCartLine[] }
>();

function getCartSnapshot(locationId: string) {
  const serialized = JSON.stringify(
    normalizeStoredItems(readStoredCarts()[locationId]),
  );
  const cached = lineCache.get(locationId);

  if (cached && cached.serialized === serialized) {
    return cached.lines;
  }

  const lines = joinCartLines(locationId);
  lineCache.set(locationId, { serialized, lines });
  return lines;
}

export function useLocationStoreCart(locationId: string) {
  const lines = useSyncExternalStore(
    subscribeToCart,
    useCallback(() => getCartSnapshot(locationId), [locationId]),
    () => EMPTY_LINES,
  );

  const addItem = useCallback(
    (productId: string, mutationIsCurrent?: () => boolean) => addLocationStoreCartItem(locationId, productId, mutationIsCurrent),
    [locationId],
  );
  const changeQuantity = useCallback(
    (productId: string, delta: number, mutationIsCurrent?: () => boolean) =>
      changeLocationStoreCartQuantity(locationId, productId, delta, mutationIsCurrent),
    [locationId],
  );

  useEffect(() => {
    readLocationStoreCartLines(locationId);
  }, [locationId]);
  const clear = useCallback(
    () => clearLocationStoreCart(locationId),
    [locationId],
  );

  return { addItem, changeQuantity, clear, lines };
}
